// Gate that hides protected routes until /api/auth/me has resolved.
// - 'unknown'       → centered loading spinner (the cookie check is in-flight).
// - 'authed'        → render children.
// - anything else   → redirect to /login (preserving the query string — see
//                      below). Covers 'unauthed' AND 'gate-expired'
//                      (sprint access-session-recovery, D2): LoginPage reads
//                      `authState` again (via App.tsx's `gateExpired` prop)
//                      to pick which screen to show; this gate's only job is
//                      "don't render the protected panel without a real
//                      session".
//
// Challenger R1 change 8 — before this sprint the fallback branch was
// `return <>{children}</>` with NO check for `authed`, so a THIRD auth
// value (`gate-expired`) would have rendered the protected panel — worse
// than today's password form. Flipped to an explicit allowlist (only
// 'authed' renders children) so any future auth value defaults SAFE
// (redirect), not open.

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { AuthState } from '../hooks/useAuth';
import { Spinner } from './atoms/Spinner';

export interface ProtectedRouteProps {
  authState: AuthState;
  children: ReactNode;
}

export function ProtectedRoute({ authState, children }: ProtectedRouteProps) {
  const location = useLocation();

  if (authState === 'unknown') {
    return (
      <div
        className="flex h-full w-full items-center justify-center bg-background"
        data-testid="auth-loading"
      >
        <Spinner size="lg" />
      </div>
    );
  }

  if (authState === 'authed') {
    return <>{children}</>;
  }

  // 'unauthed' | 'gate-expired' — forward the search string (e.g. `?reauth=`
  // from the D3 re-entry navigation) so LoginPage can tell EC-3 apart from a
  // fresh gate expiry (challenger R1 change 5).
  return <Navigate to={{ pathname: '/login', search: location.search }} replace />;
}
